import { SendIcon } from '@diligentcorp/atlas-react-icons';
import { IssueStatusChip } from '@issuemanager/apps/client/components';
import {
  AppHeaderPrimaryButtonProps,
  useSetHeaderProps,
} from '@issuemanager/apps/client/contexts';
import { getIssueHeaderTitle } from '@issuemanager/apps/client/utils';
import { useSaveIssue } from '@issuemanager/client/features/issues';
import { IssueStatusEnum, IssueTypeEnum } from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIssueStatusInfo } from './use-issue-status-info';
import { AssignIssueFormValues } from './use-manage-issue-form';
import useResetFormData from './use-reset-form-data';
import { useIssueOutletContext } from './useIssueOutletContext';

const workflowActionLookup = {
  saveAndAccept: 'issue:actions.saveAndAccept',
  accept: 'issue:actions.accept',
  approve: 'issue:actions.approve',
  saveAndApprove: 'issue:actions.saveAndApprove',
  publish: 'issue:actions.publish',
} as const;

const submitActionLookup = {
  ongoing: 'issue:actions.submitting',
  base: 'issue:actions.submit',
  success: 'issue:actions.submitSuccess',
} as const;

const declineActionLookup = {
  ongoing: 'issue:actions.decliningFromInReview',
  base: 'issue:actions.declineFromInReview',
  success: 'issue:actions.declineFromInReviewSuccess',
} as const;

type Params = {
  manageIssueForm: UseFormReturn<
    AssignIssueFormValues,
    any,
    AssignIssueFormValues
  >;
  assignIssueIsLoading: boolean;
  initiateWorkflowIsLoading: boolean;
  manageIssueHandleSubmit: (submitCallback?: () => Promise<void>) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isLoading: boolean;
  activateDeclinePanel: () => void;
  activateApprovePanel: () => void;
  handleSave: () => void;
  saveIssue: ReturnType<typeof useSaveIssue>;
  handleSubmitToInReview?: () => void;
  handleMoveToDraft?: () => void;
  submitToInReviewIsPending: boolean;
  moveToDraftIsPending: boolean;
  handleApproveForPublishing?: () => void;
  approveForPublishingIsPending: boolean;
  handlePublishIssue?: () => void;
  publishIssueIsPending: boolean;
  activateDeclineToDraftPanel?: () => void;
  declineToDraftIsPending: boolean;
};

export const useHeaderButtons = ({
  manageIssueForm,
  assignIssueIsLoading,
  initiateWorkflowIsLoading,
  manageIssueHandleSubmit,
  isEditing,
  setIsEditing,
  isLoading,
  activateDeclinePanel,
  activateApprovePanel,
  handleSave,
  saveIssue,
  handleSubmitToInReview,
  handleMoveToDraft,
  submitToInReviewIsPending,
  moveToDraftIsPending,
  handleApproveForPublishing,
  approveForPublishingIsPending,
  handlePublishIssue,
  publishIssueIsPending,
  activateDeclineToDraftPanel,
  declineToDraftIsPending,
}: Params): void => {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const {
    code,
    issue,
    userRoles: issueUserRoles,
    canDiscardIssue,
    onDiscardIssue,
  } = useIssueOutletContext();
  const setHeaderProps = useSetHeaderProps();
  const isAuditFinding =
    issue.data?.issueType?.name === IssueTypeEnum.AUDIT_FINDING;

  const {
    isInitiated,
    isClosedOrDiscarded,
    isNotYetAssigned,
    isReadyToApprove,
  } = useIssueStatusInfo({
    status: issue.data?.status,
    isInitiated: issue.data?.isInitiated,
    approvalStatus: issue.data?.approvalStatus,
  });

  const { resetForm } = useResetFormData({
    manageIssueForm,
  });

  const handleCancel = useEventCallback(() => {
    setIsEditing(false);
    resetForm();
  });

  const disabledBtns = isLoading;

  const assignButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    let assignButtonLabel: string;
    if (isEditing) {
      assignButtonLabel = isAuditFinding
        ? t(workflowActionLookup.saveAndAccept)
        : t(workflowActionLookup.saveAndApprove);
    } else {
      assignButtonLabel = isAuditFinding
        ? t(workflowActionLookup.accept)
        : t(workflowActionLookup.approve);
    }

    return {
      id: 'assign-button',
      variant: 'contained',
      disabled: disabledBtns,
      loading: assignIssueIsLoading,
      onClick: () => manageIssueHandleSubmit(),
      children: assignButtonLabel,
      'data-testid': 'assign-button',
    };
  }, [
    isEditing,
    disabledBtns,
    assignIssueIsLoading,
    manageIssueHandleSubmit,
    isAuditFinding,
    t,
  ]);

  const initiateButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    const getInitiateButtonLabel = (): string => {
      const canInitiate = isEditing && !isLoading;
      if (initiateWorkflowIsLoading) {
        return canInitiate
          ? t('issue:actions.savingAndInitiating')
          : t('issue:actions.initiating');
      }
      return canInitiate
        ? t('issue:actions.saveAndInitiate')
        : t('issue:actions.initiate');
    };

    return {
      id: 'initiate-button',
      variant: 'contained',
      disabled: disabledBtns,
      loading: initiateWorkflowIsLoading,
      startIcon: <SendIcon />,
      onClick: () => manageIssueHandleSubmit(),
      children: getInitiateButtonLabel(),
      'data-testid': 'initiate-button',
    };
  }, [
    isLoading,
    isEditing,
    initiateWorkflowIsLoading,
    disabledBtns,
    manageIssueHandleSubmit,
  ]);

  const cancelButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'cancel-button',
      variant: 'text',
      onClick: handleCancel,
      children: t('issue:actions.cancel'),
      'data-testid': 'cancel-button',
      disabled: disabledBtns,
    }),
    [disabledBtns, handleCancel],
  );

  const saveButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'save-button',
      variant: 'outlined',
      disabled: disabledBtns,
      onClick: handleSave,
      loading: saveIssue.isPending,
      children: saveIssue.isPending
        ? t('issue:actions.saving')
        : t('issue:actions.save'),
      'data-testid': 'save-button',
    }),
    [disabledBtns, handleSave, saveIssue.isPending],
  );

  const editButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'edit-button',
      variant: 'outlined',
      disabled: disabledBtns,
      onClick: () => setIsEditing(true),
      children: t('issue:actions.edit'),
      'data-testid': 'edit-button',
    }),
    [disabledBtns, setIsEditing],
  );

  const discardButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.discard'),
      variant: 'text',
      'data-testid': 'discard-button',
      onClick: onDiscardIssue,
      disabled: disabledBtns,
    }),
    [disabledBtns, onDiscardIssue],
  );

  const approveButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.approve'),
      variant: 'contained',
      'data-testid': 'approve-button',
      onClick: activateApprovePanel,
      disabled: disabledBtns,
    }),
    [disabledBtns],
  );

  const declineButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.decline'),
      variant: 'outlined',
      'data-testid': 'decline-button',
      onClick: activateDeclinePanel,
      disabled: disabledBtns,
    }),
    [disabledBtns],
  );

  const moveToOpenButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: submitToInReviewIsPending
        ? t(submitActionLookup.ongoing)
        : t(submitActionLookup.base),
      variant: 'contained',
      'data-testid': 'move-to-open-button',
      onClick: handleSubmitToInReview,
      disabled: disabledBtns,
      loading: submitToInReviewIsPending,
    }),
    [disabledBtns, handleSubmitToInReview, t, submitToInReviewIsPending],
  );

  const moveToDraftButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: moveToDraftIsPending
        ? t(declineActionLookup.ongoing)
        : t(declineActionLookup.base),
      variant: 'outlined',
      'data-testid': 'move-to-draft-button',
      onClick: handleMoveToDraft,
      disabled: disabledBtns,
      loading: moveToDraftIsPending,
    }),
    [disabledBtns, handleMoveToDraft, t, moveToDraftIsPending],
  );

  const approveForPublishingButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t(workflowActionLookup.approve),
      variant: 'contained',
      'data-testid': 'approve-for-publishing-button',
      onClick: handleApproveForPublishing,
      disabled: disabledBtns,
      loading: approveForPublishingIsPending,
    }),
    [
      disabledBtns,
      handleApproveForPublishing,
      t,
      approveForPublishingIsPending,
    ],
  );

  const publishButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t(workflowActionLookup.publish),
      variant: 'contained',
      'data-testid': 'publish-issue-button',
      onClick: handlePublishIssue,
      disabled: disabledBtns,
      loading: publishIssueIsPending,
    }),
    [disabledBtns, handlePublishIssue, t, publishIssueIsPending],
  );

  const declineToDraftButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.declineToDraft'),
      variant: 'outlined',
      'data-testid': 'decline-to-draft-button',
      onClick: activateDeclineToDraftPanel,
      disabled: disabledBtns,
      loading: declineToDraftIsPending,
    }),
    [disabledBtns, activateDeclineToDraftPanel, declineToDraftIsPending, t],
  );

  const isDraftIssue = issue.data?.status === IssueStatusEnum.DRAFT;
  const isIssueOpen =
    issue.data?.status === IssueStatusEnum.IN_REVIEW ||
    issue.data?.status === IssueStatusEnum.OPEN;
  const isToBePublished =
    issue.data?.status === IssueStatusEnum.TO_BE_PUBLISHED;
  const isPendingAcceptance =
    issue.data?.status === IssueStatusEnum.PENDING_ACCEPTANCE;
  const isIssueCreator = Boolean(issueUserRoles.data?.isCreator);
  const isIssueTypeManager = Boolean(issueUserRoles.data?.isIssueTypeManager);
  const isIssueOwner = Boolean(issueUserRoles.data?.isOwner);
  const isApprover = Boolean(issueUserRoles.data?.isApprover);
  const isNotYetInitiated =
    issue.data?.status === IssueStatusEnum.REMEDIATION_PLANNING ||
    (issue.data?.status === IssueStatusEnum.IN_PROGRESS &&
      !issue.data?.isInitiated);

  const canManageDraft = isIssueCreator || isIssueTypeManager;

  const confirmButton: AppHeaderPrimaryButtonProps | null = useMemo(() => {
    if (isAuditFinding && isToBePublished && isIssueTypeManager) {
      if (isEditing) {
        return null;
      }
      return publishButtonProps;
    }
    if (isAuditFinding && isIssueOpen && isIssueTypeManager) {
      if (isEditing) {
        return null;
      }
      return approveForPublishingButtonProps;
    }
    if (isNotYetAssigned || assignIssueIsLoading) {
      if (isAuditFinding) {
        if (isIssueOwner) {
          return assignButtonProps;
        }
      } else if (isIssueTypeManager) {
        return assignButtonProps;
      }
    } else if (isNotYetInitiated) {
      if (isIssueTypeManager || isIssueOwner) {
        return initiateButtonProps;
      }
    }

    return null;
  }, [
    assignButtonProps,
    initiateButtonProps,
    approveForPublishingButtonProps,
    publishButtonProps,
    isNotYetAssigned,
    isNotYetInitiated,
    isIssueTypeManager,
    isIssueOwner,
    assignIssueIsLoading,
    isAuditFinding,
    isIssueOpen,
    isToBePublished,
    isEditing,
  ]);

  const editingButtonsProps: AppHeaderPrimaryButtonProps[] = useMemo(() => {
    const getButtonsForEditableIssue = (): AppHeaderPrimaryButtonProps[] =>
      isEditing ? [cancelButtonProps, saveButtonProps] : [editButtonProps];

    if (isDraftIssue) {
      return canManageDraft ? getButtonsForEditableIssue() : [];
    }

    const isAuthorizedToEdit = isIssueTypeManager || isIssueOwner;

    const isInEditableState =
      isInitiated || (!isInitiated && !isClosedOrDiscarded);

    const hasEditPermission = isAuthorizedToEdit && isInEditableState;

    return hasEditPermission ? getButtonsForEditableIssue() : [];
  }, [
    cancelButtonProps,
    editButtonProps,
    isIssueTypeManager,
    isClosedOrDiscarded,
    isEditing,
    isInitiated,
    isIssueOwner,
    saveButtonProps,
    isDraftIssue,
    canManageDraft,
  ]);

  useEffect(() => {
    const issueData = issue.data;
    if (!issueData || issueUserRoles.isFetching) return;

    let buttons: AppHeaderPrimaryButtonProps[] = [];

    if (isDraftIssue) {
      buttons = [
        ...(canDiscardIssue && !isEditing && canManageDraft
          ? [discardButtonProps]
          : []),
        ...editingButtonsProps,
        ...(moveToOpenButtonProps && !isEditing && isIssueCreator
          ? [moveToOpenButtonProps]
          : []),
      ];
    } else {
      buttons = [
        ...(isReadyToApprove && isApprover && !isEditing
          ? [declineButtonProps]
          : []),
        ...(isReadyToApprove && isApprover && !isEditing
          ? [approveButtonProps]
          : []),
        ...(canDiscardIssue && !isEditing ? [discardButtonProps] : []),
        ...editingButtonsProps,
        ...(isIssueOpen && isIssueTypeManager && !isEditing
          ? [moveToDraftButtonProps]
          : []),
        ...(activateDeclineToDraftPanel &&
        isAuditFinding &&
        !isEditing &&
        ((isPendingAcceptance && isIssueOwner) ||
          (isToBePublished && isIssueTypeManager))
          ? [declineToDraftButtonProps]
          : []),
        ...(confirmButton ? [confirmButton] : []),
      ];
    }

    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [{ url: '/', title: t('common:appName') }],
        title: getIssueHeaderTitle({
          code,
          title: issueData.title,
        }),
      },
      chip: issueData.status ? (
        <IssueStatusChip
          status={issueData.status}
          isInitiated={issueData.isInitiated}
        />
      ) : undefined,
      buttons,
    });
  }, [
    canDiscardIssue,
    code,
    confirmButton,
    declineButtonProps,
    declineToDraftButtonProps,
    discardButtonProps,
    editingButtonsProps,
    isApprover,
    isAuditFinding,
    isDraftIssue,
    isEditing,
    isIssueCreator,
    isIssueOpen,
    isIssueOwner,
    isIssueTypeManager,
    isPendingAcceptance,
    isReadyToApprove,
    isToBePublished,
    issue.dataUpdatedAt,
    issueUserRoles.isFetching,
    moveToDraftButtonProps,
    moveToOpenButtonProps,
    canManageDraft,
    approveButtonProps,
    publishButtonProps,
  ]);
};
